import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Phone, UserPlus, Search, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const INTEREST = { mentor: ["Wants to mentor", "bg-primary/10 text-primary", UserPlus], mentee: ["Looking for a mentor", "bg-emerald-100 text-emerald-700", Search], general: ["General", "bg-slate-100 text-slate-600", MessageSquare] };
const STATUS = { new: "bg-amber-100 text-amber-800", contacted: "bg-sky-100 text-sky-700", closed: "bg-slate-100 text-slate-500" };
const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const ContactRow = ({ c, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [label, tone, Icon] = INTEREST[c.interest] || INTEREST.general;
  const setStatus = async (status) => {
    setBusy(true);
    try { await api.put(`/admin/contacts/${c.contact_id}`, { status }); onChange(); }
    catch { toast.error("Could not update"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap gap-4 items-start" data-testid={`contact-${c.contact_id}`}>
      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-slate-600" /></div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-heading font-bold text-slate-900">{c.name}</p>
          <span className={`font-body text-[11px] px-2 py-0.5 rounded-full ${tone}`}>{label}</span>
          <span className={`font-body text-[11px] px-2 py-0.5 rounded-full ${STATUS[c.status] || STATUS.new}`} data-testid={`contact-status-${c.contact_id}`}>{c.status}</span>
          <span className="font-body text-xs text-slate-400">{fmt(c.created_at)}</span>
        </div>
        <div className="flex flex-wrap gap-3 mt-1 font-body text-xs text-slate-500">
          <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-primary"><Mail className="w-3.5 h-3.5" /> {c.email}</a>
          {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-primary"><Phone className="w-3.5 h-3.5" /> {c.phone}</a>}
        </div>
        {(c.skills || c.experience) && <p className="font-body text-xs text-slate-600 mt-2"><span className="font-semibold">Skills:</span> {c.skills || "—"} · <span className="font-semibold">Experience:</span> {c.experience || "—"}</p>}
        <p className="font-body text-sm text-slate-700 mt-2 whitespace-pre-line bg-slate-50 rounded-lg px-3 py-2">{c.profile}</p>
      </div>
      <div className="flex gap-1">
        {c.status !== "contacted" && <Button size="sm" variant="outline" onClick={() => setStatus("contacted")} disabled={busy} className="rounded-lg" data-testid={`contact-mark-contacted-${c.contact_id}`}>Mark contacted</Button>}
        {c.status !== "closed" && <Button size="sm" variant="ghost" onClick={() => setStatus("closed")} disabled={busy} className="rounded-lg" data-testid={`contact-close-${c.contact_id}`}>Close</Button>}
      </div>
    </div>
  );
};

export const ContactsPanel = () => {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("new");
  const load = () => api.get("/admin/contacts").then((r) => setRows(r.data)).catch(() => toast.error("Could not load messages"));
  useEffect(() => { load(); }, []);
  const list = (rows || []).filter((c) => filter === "all" || c.status === filter);
  const n = (s) => (rows || []).filter((c) => c.status === s).length;
  return (
    <div data-testid="contacts-panel">
      <div className="inline-flex rounded-xl bg-slate-100 p-1">
        {[["new", `New (${n("new")})`], ["contacted", `Contacted (${n("contacted")})`], ["closed", "Closed"], ["all", "All"]].map(([k, t]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`contacts-filter-${k}`}
            className={`px-3.5 py-1.5 rounded-lg font-heading font-bold text-xs transition-colors ${filter === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{t}</button>
        ))}
      </div>
      {!rows ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : list.length === 0 ? <div className="mt-6 rounded-2xl border border-dashed border-slate-300 p-10 text-center font-body text-sm text-slate-500" data-testid="contacts-empty">No {filter === "all" ? "" : filter} messages.</div>
        : <div className="space-y-3 mt-6">{list.map((c) => <ContactRow key={c.contact_id} c={c} onChange={load} />)}</div>}
    </div>
  );
};
